import { ONBOARDING_TOUR_SEEN_KEY } from "@/constants/tour";
import type { AccountDeepLink } from "@/interfaces/accountNav";
import type {
  CardPosition,
  Size,
  TourEligibilityInput,
  TourPlacement,
  TourStep,
  WalletDeepLinkTab,
  WalletTourDestination,
  WalletTourDestinationInput,
} from "@/interfaces/tour";
import type { SpotlightRect } from "@/interfaces/tutorial";

/** Clamps one axis to `margin` from both edges. A card larger than the viewport lands on the
 * margin rather than off-screen, because both bounds collapse to `margin`. */
function clampAxis(value: number, size: number, viewportSize: number, margin: number): number {
  const max = Math.max(margin, viewportSize - size - margin);
  return Math.min(Math.max(value, margin), max);
}

export function unionRects(rects: readonly SpotlightRect[]): SpotlightRect | null {
  if (rects.length === 0) return null;

  let top = Number.POSITIVE_INFINITY;
  let left = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;

  for (const rect of rects) {
    top = Math.min(top, rect.top);
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.left + rect.width);
    bottom = Math.max(bottom, rect.top + rect.height);
  }

  return { top, left, width: right - left, height: bottom - top };
}

export function padRect(rect: SpotlightRect, pad: number): SpotlightRect {
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

export function requiredTargets(step: TourStep): readonly string[] {
  const optional = new Set(step.optionalTargets ?? []);
  return step.targets.filter((id) => !optional.has(id));
}

export function centerCard(card: Size, viewport: Size): CardPosition {
  return {
    top: Math.round((viewport.height - card.height) / 2),
    left: Math.round((viewport.width - card.width) / 2),
  };
}

export function clampToViewport(
  pos: CardPosition,
  card: Size,
  viewport: Size,
  margin: number,
): CardPosition {
  return {
    top: clampAxis(pos.top, card.height, viewport.height, margin),
    left: clampAxis(pos.left, card.width, viewport.width, margin),
  };
}

/** Card position relative to the TARGET rect (union without pad), then clamped to the viewport. */
export function placeCard(
  target: SpotlightRect,
  place: TourPlacement,
  card: Size,
  viewport: Size,
  gap: number,
  margin: number,
): CardPosition {
  const raw = ((): CardPosition => {
    switch (place) {
      case "right":
        return { left: target.left + target.width + gap, top: target.top };
      case "above":
        return { top: target.top - card.height - gap, left: target.left };
      case "left":
        return { left: target.left - card.width - gap, top: target.top };
      case "below":
        return {
          top: target.top + target.height + gap,
          left: target.left + target.width - card.width,
        };
    }
  })();

  const clamped = clampToViewport(raw, card, viewport, margin);
  return { top: Math.round(clamped.top), left: Math.round(clamped.left) };
}

/**
 * Whether a placement measured for step `placedIndex` may be painted for the step now on screen.
 *
 * Every argument is known during render, which is the whole point. `index` and `isSettling` change
 * in the SAME commit as the step's copy, while a placement is written by a passive effect — one
 * that runs after React has committed and the browser has painted. Painting a placement without
 * this check shows the new step's badge, title and body at the previous step's coordinates, with
 * the previous element still lit, for a frame.
 *
 * It cannot see the other half of that problem — a SECOND run over the same index, whose stale
 * placement carries a matching `index`. The overlay handles that one by dropping the measurement
 * when a run starts, settling pass included, so nothing stale is left for this to accept.
 */
export function paintsMeasuredPlacement(
  placedIndex: number | null,
  index: number,
  isSettling: boolean,
): boolean {
  return !isSettling && placedIndex === index;
}

/**
 * DOM id of the Account page's "Groups" tab button — the wallet step's fallback target.
 *
 * NOT in `TOUR_TARGET_IDS` yet because nothing renders it: OrgDetail.tsx:544 gives an id to the
 * Users tab only, and that file is owned by another change. Until the id is added there this
 * target simply never resolves, which is exactly what the same case does today.
 */
export const ACCOUNT_GROUPS_TAB_TARGET_ID = "tour-account-tab-groups";

/**
 * Whether an organization shows the GROUPS tree at all.
 *
 * MIRRORS `showGroups` at OrgDetail.tsx:425-430, which is the source of truth. It is duplicated
 * here on purpose: the tour has to name the tab its deep link asks for BEFORE OrgDetail mounts,
 * and OrgDetail neither exports the rule nor accepts a tab it has not computed. If the rule moves
 * there, move this with it.
 */
export function orgShowsGroups(
  i: Pick<WalletTourDestinationInput, "isGfg" | "hasMultiGroupEntitlement" | "groupCount">,
): boolean {
  return i.isGfg || i.hasMultiGroupEntitlement || i.groupCount > 1;
}

/**
 * Where the tour's wallet step has to land for this organization.
 *
 * Tree hidden: the root group's Wallet is raised to org level (the `wallets` OrgTab) under the
 * same two conditions OrgDetail puts on the raised tabs at OrgDetail.tsx:461-464 — a root group
 * exists, and the caller may `view` CREDIT.
 * Tree shown: the wallet is a group sub-tab, reached through Groups → the group → Wallet.
 * Neither: `groups`, so the step still has a real control to light. Asking for a tab this
 * organization does not have is the link OrgDetail drops at OrgDetail.tsx:471, which leaves the
 * step waiting on an element that never mounts.
 */
export function walletTourDestination(i: WalletTourDestinationInput): WalletTourDestination {
  if (!orgShowsGroups(i)) {
    const isRaised = i.rootGroupId !== null && i.canViewCredit;
    return { kind: isRaised ? "org-wallet" : "groups", groupId: null };
  }
  const groupId = i.preferredGroupId ?? i.rootGroupId;
  return groupId === null ? { kind: "groups", groupId: null } : { kind: "group-wallet", groupId };
}

/**
 * The wallet step pointed at targets that exist for this organization. Both wallet arrangements
 * render the same panel, so only the unreachable case needs different targets: the Groups tab, so
 * the step spotlights a real control instead of timing out into a card over nothing.
 */
export function walletTourStep<T extends TourStep>(step: T, dest: WalletTourDestination): T {
  if (dest.kind !== "groups") return step;
  return { ...step, targets: [ACCOUNT_GROUPS_TAB_TARGET_ID] };
}

/** OrgDetail tab keys used below — typed to `WalletDeepLinkTab` so a wrong/typo'd literal on
 * either line fails `tsc`, not just this file's tests. */
const ACCOUNT_TAB_GROUPS: WalletDeepLinkTab = "groups";
const ACCOUNT_TAB_WALLETS: WalletDeepLinkTab = "wallets";
/** GroupView sub-tab key for the "Wallet" tab. It is NOT "wallets" — that key lands on another tab. */
const GROUP_TAB_WALLETS = "credits-pool";

/**
 * The deep link that opens this organization's wallet, minus the `nonce` the caller stamps.
 *
 * A single-group client has no Groups tab at all — its wallet is the org-level "Wallet" tab — so
 * the link that works for one arrangement is a link OrgDetail silently drops for the other.
 */
export function walletDeepLink(dest: WalletTourDestination): AccountDeepLink {
  if (dest.kind === "org-wallet") return { tab: ACCOUNT_TAB_WALLETS };
  // No group to open: still land on the tree, which is where the wallet would be.
  if (dest.groupId === null) return { tab: ACCOUNT_TAB_GROUPS };
  return { tab: ACCOUNT_TAB_GROUPS, groupId: dest.groupId, groupTab: GROUP_TAB_WALLETS };
}

export function isTourEligible(i: TourEligibilityInput): boolean {
  return i.isLoaded && !i.isGfg && !i.isImpersonating && !i.isMobile && i.canEditCompany;
}

export function hasSeenOnboardingTour(metadata: unknown): boolean {
  if (typeof metadata !== "object" || metadata === null) return false;
  return (metadata as Record<string, unknown>)[ONBOARDING_TOUR_SEEN_KEY] === true;
}
